import {
  useJobcardStatusChoices,
  usePriorityOptions,
} from "@features/ax/hooks";
import { ServiceOrderFilterPanelFilters } from "@features/ax/srvc-and-mgmt/service-order/types";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import React, { useEffect, useMemo, useState } from "react";
import { useGetAllOperationType } from "../../../hooks";

interface FilterOptions {
  statuses?: Array<{ value: string; label: string }>;
  priorities?: Array<{ value: string; label: string }>;
  assignedTo?: Array<{ value: string; label: string }>;
  orderTypes?: Array<{ value: string; label: string }>;
}

interface ServiceOrdersFilterPanelProps {
  currentFilters: ServiceOrderFilterPanelFilters;
  onApplyFilters: (filters: ServiceOrderFilterPanelFilters) => void;
  onResetFilters: () => void;
  filterOptions?: FilterOptions;
}

export const ServiceOrdersFilterPanel: React.FC<
  ServiceOrdersFilterPanelProps
> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
  filterOptions = {},
}) => {
  // Local state for temporary filter values
  const [tempFilters, setTempFilters] = useState<{
    status: string;
    priority: string;
    assignedTo: string;
    orderType: string;
  }>({
    status: currentFilters?.status || "all",
    priority: currentFilters?.priority || "all",
    assignedTo: currentFilters?.assignedTo || "all",
    orderType: currentFilters?.orderType || "all",
  });

  const { data: priorityList } = usePriorityOptions();
  const { data: orderTypeList } = useGetAllOperationType();
  const { data: statusList } = useJobcardStatusChoices();

  const statusOptions = useMemo(() => {
    return (
      statusList?.map((status: any) => ({
        value: status.key,
        label: status.value,
      })) || []
    );
  }, [statusList]);

  const priorityOptions = useMemo(() => {
    return (
      priorityList?.map((assetType: any) => ({
        value: assetType.key,
        label: assetType.value,
      })) || []
    );
  }, [priorityList]);

  const orderTypeOptions = useMemo(() => {
    return (
      orderTypeList?.map((assetType: any) => ({
        value: assetType.key,
        label: assetType.value,
      })) || []
    );
  }, [orderTypeList]);

  // Assigned To options
  const assignedToOptions = [];

  // Create form fields
  const createFormFields = (): FormField[] => [
    {
      name: "status",
      type: "select",
      label: "Status",
      options: statusOptions,
      placeholder: "Select status",
      classes: FIELD_CLASSES,
    },
    {
      name: "priority",
      type: "select",
      label: "Priority",
      options: priorityOptions,
      placeholder: "Select priority",
      classes: FIELD_CLASSES,
    },
    {
      name: "orderType",
      type: "select",
      label: "Order Type",
      options: orderTypeOptions,
      placeholder: "Select order type",
      classes: FIELD_CLASSES,
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: createFormFields(),
    defaultValues: tempFilters,
  });

  // Update form values when current filters change
  useEffect(() => {
    const newTempFilters = {
      status: currentFilters?.status || "all",
      priority: currentFilters?.priority || "all",
      assignedTo: currentFilters?.assignedTo || "all",
      orderType: currentFilters?.orderType || "all",
    };

    setTempFilters(newTempFilters);

    // Reset form with new values
    form.reset(newTempFilters);
  }, [currentFilters]);

  // Handle form submission
  const handleSubmit = () => {
    const filtersToApply: ServiceOrderFilterPanelFilters = {};
    const data = form.getValues();

    if (data.status && data.status !== "all") {
      filtersToApply.status = data.status;
    }
    if (data.priority && data.priority !== "all") {
      filtersToApply.priority = data.priority;
    }
    if (data.assignedTo && data.assignedTo !== "all") {
      filtersToApply.assignedTo = data.assignedTo;
    }
    if (data.orderType && data.orderType !== "all") {
      filtersToApply.orderType = data.orderType;
    }

    onApplyFilters(filtersToApply);
  };

  // Handle reset
  const handleReset = () => {
    const defaultFilters = {
      status: "all",
      priority: "all",
      assignedTo: "all",
      orderType: "all",
    };
    setTempFilters(defaultFilters);
    form.reset(defaultFilters);
    onResetFilters();
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    setTempFilters((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Custom footer with Apply and Reset buttons
  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-2 lg:col-span-4 flex justify-end gap-2">
      <Button type="button" variant="outline" onClick={handleReset}>
        Reset Filters
      </Button>
      <Button onClick={() => handleSubmit()}>Apply Filters</Button>
    </div>
  );

  return (
    <DynamicForm
      fields={createFormFields()}
      form={form}
      config={{
        showSubmitButton: false, // We're using custom footer
        showResetButton: false, // We're using custom footer
        layout: "vertical",
        spacing: "medium",
      }}
      gridColumns={4}
      responsiveColumns={{ sm: 1, md: 2, lg: 3 }}
      className="space-y-6"
      renderCustomFooter={renderCustomFooter}
    />
  );
};
